import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.2.
 *
 * The `Omit<…, 'size'>` is not cosmetic: `size` already exists as a native
 * `<input>` attribute and means "width in characters". Without the Omit,
 * TypeScript accepts `size="sm"` and the browser discards it silently — this
 * file's own test pins that down.
 */
export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'size'> {
  /** Paints the border. `FormField` sets the `aria-invalid`, not this component. */
  invalid?: boolean;
  size?: 'sm' | 'md';
  /** Ornaments inside the field: a magnifier, a unit suffix, a spinner. */
  startSlot?: React.ReactNode;
  endSlot?: React.ReactNode;
}

/**
 * `h-row` / `h-row-compact` (48px / 36px) are the semantic roles for control
 * height, not a hand-written `h-9`/`h-8` — they are declared in `theme.css` for
 * exactly this use («Medidas de componente»). `px-inset` / `px-inset-sm` do the
 * same for the horizontal padding.
 *
 * `hasStartSlot`/`hasEndSlot` reserve space (`pl-9`/`pr-9`, on the ornament's
 * side) so the typed text never sits under the icon: the numeric width here is
 * not a design measure, it is the physical gap the ornament is positioned in,
 * which still measures `size-icon-sm` with padding of its own — see below.
 * Tailwind's numeric scale is deliberately left intact for this (`theme.css`,
 * the «Espaciado» comment).
 */
const inputVariants = cva(
  'w-full min-w-0 rounded-md border bg-surface text-fg transition-colors placeholder:text-fg-muted focus-ring disabled:cursor-not-allowed disabled:border-border disabled:bg-page disabled:text-fg-muted',
  {
    variants: {
      size: {
        md: 'h-row px-inset text-body-s',
        sm: 'h-row-compact px-inset-sm text-caption',
      },
      // The border alone: the `aria-invalid` (the announcement for screen
      // readers) is set by `FormField`, which also ties the error message
      // through `aria-describedby` — that is where the text accompanying this
      // colour lives, so the icon/text+colour pairing (WCAG 1.4.1) is satisfied
      // by the field as a whole, not by this component in isolation.
      invalid: {
        true: 'border-danger',
        false: 'border-border',
      },
      hasStartSlot: {
        true: 'pl-9',
        false: '',
      },
      hasEndSlot: {
        true: 'pr-9',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      invalid: false,
      hasStartSlot: false,
      hasEndSlot: false,
    },
  },
);

/**
 * `forwardRef<HTMLInputElement>` is mandatory: React Hook Form registers the
 * field by the ref, not by an intercepted `value` prop.
 *
 * The slots do NOT wrap the `<input>` in a surrounding `<div>` that also carries
 * the border: `<input>` is a void element (it can have no children), so the
 * ornaments are positioned `absolute` over a `relative` container, but the
 * border, the background and the focus ring (`focus-ring`) keep living on the
 * `<input>` itself, which takes up 100% of the container's width. That is what
 * avoids two problems at once:
 * - The focus ring encloses the whole field, ornaments included — not a narrow
 *   box that ignores the icon.
 * - The typed text never sits under the ornament: the `<input>` reserves the gap
 *   with `pl-9`/`pr-9` (see `inputVariants`) and the field's real content starts
 *   after that reservation.
 *
 * The leading ornament (`startSlot`) carries `pointer-events-none`: in the three
 * cases the contract names — magnifier, unit suffix, spinner — it is decorative,
 * and without this a click on the icon does not reach the `<input>` beneath it.
 * The trailing ornament (`endSlot`) does NOT carry it: the contract explicitly
 * asks that a clear button there be reachable, and `pointer-events-none` on the
 * container is inherited by its children and would make an interactive button
 * inert. Coming after the `<input>` in the DOM, an interactive `endSlot` also
 * ends up on top in the stacking order (never "trapped behind" the field) and
 * keeps its own place in the tab order, with no extra trick.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input(
    { className, invalid = false, size = 'md', startSlot, endSlot, ...props },
    ref,
  ) {
    return (
      <div className="relative w-full">
        {startSlot ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-inset-sm text-fg-muted"
          >
            {startSlot}
          </span>
        ) : null}

        <input
          ref={ref}
          className={cn(
            inputVariants({
              size,
              invalid,
              hasStartSlot: Boolean(startSlot),
              hasEndSlot: Boolean(endSlot),
            }),
            className,
          )}
          {...props}
        />

        {endSlot ? (
          <span className="absolute inset-y-0 right-0 flex items-center pr-inset-sm text-fg-muted">
            {endSlot}
          </span>
        ) : null}
      </div>
    );
  },
);
